import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { useToast } from '@cad-arc';
import {
    AttachmentsResource,
    EntityTypeEnumType,
    LinkTypesEnum,
    DeleteAttachmentRequest,
    DeleteAttachmentResponse,
    AttachmentDto,
    GetAttachmentsResponse,
} from '@mark43/cad-api';
import { logAttachmentsError } from '../helpers';
import { ATTACHMENTS_QUERY_KEY } from '../constants';
import { AttachmentsPanelType } from '../types';

type UseAttachmentsBaseParameter = {
    entityId: number;
    entityType: AttachmentsPanelType;
};

type DeleteAttachmentInput = {
    attachmentId: number;
    attachmentType: AttachmentDto['attachmentType'];
    entityId: number;
};

export function useDeleteAttachment({ entityId, entityType }: UseAttachmentsBaseParameter) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation<DeleteAttachmentResponse | undefined, Error, DeleteAttachmentInput>({
        mutationFn: async ({
            attachmentId,
            attachmentType,
            entityId,
        }: DeleteAttachmentInput): Promise<DeleteAttachmentResponse | undefined> => {
            const linkType =
                entityType === 'BOLO'
                    ? LinkTypesEnum.BOLO_ATTACHMENT
                    : LinkTypesEnum.CAD_AGENCY_EVENT_ATTACHMENT;

            const request: DeleteAttachmentRequest = {
                attachmentId,
                attachmentType,
                entityId,
                entityType: entityType as EntityTypeEnumType,
                linkType,
            };

            const response = await apiClient.request<AttachmentsResource.DeleteAttachment>({
                method: 'POST',
                url: 'attachments/delete',
                data: request,
            });

            return response.data?.data;
        },
        onSuccess: (_, { attachmentId }) => {
            // Remove the deleted attachment from the query cache
            queryClient.setQueryData(
                [ATTACHMENTS_QUERY_KEY, entityId],
                (
                    oldData: GetAttachmentsResponse | undefined
                ): GetAttachmentsResponse | undefined => {
                    if (!oldData) return oldData;

                    const removeDeletedAttachment = (attachments: AttachmentDto[]) => {
                        return attachments.filter(
                            (attachment) => attachment.attachmentId !== attachmentId
                        );
                    };

                    // Filter out the deleted attachment from parent attachments array
                    const updatedAttachments = removeDeletedAttachment(oldData.attachments);

                    // Filter out the deleted attachment from merged attachments array
                    const updatedMergedAttachments = removeDeletedAttachment(
                        oldData.mergedAttachments
                    );

                    return {
                        attachments: updatedAttachments,
                        mergedAttachments: updatedMergedAttachments,
                    };
                }
            );
        },
        onError: (error) => {
            logAttachmentsError(error as Error, 'delete-attachment');
            toast({
                status: 'error',
                description: 'Failed to delete attachment',
            });
            queryClient.invalidateQueries([ATTACHMENTS_QUERY_KEY, entityId]);
        },
    });
}
